import { AttachmentImage } from './conversation';
import {
  EFollowType,
  IAuthor,
  IBackgroundImg,
  IExtraInfo,
  IImInfo,
  IModerationStatus,
  INpcAvatarInfo,
  IPrivilegeInfo,
  IStatisticInfo,
} from './feed';
import { ITagInfo } from './search';

export enum MessageType {
  Text = 'text',
  Voice = 'voice',
  Aside = 'aside',
  Reset = 'reset',
  Intro = 'intro',
  Attachment = 'attachment',
}

export interface IAsideMsgTextInfo {
  text: string;
  schema: string;
}

export enum RoleType {
  AI = 'ai',
  User = 'user',
}

export enum AudioStatus {
  Ready = 1,
  Playing = 2,
  Loading = 3,
  Reload = 4,
  Disable = 5,
}

export interface IMessage {
  mid: string;
  localMid?: string;
  txt?: string;
  think_process?: string;
  type: MessageType;
  text_info?: IAsideMsgTextInfo[];
  role_type: RoleType;
  voice_uri?: string;
  voice_duration_ms?: number;
  wav_status?: AudioStatus;
  reply_for?: string; // 回复的消息mid
  loading?: boolean;
  discarded?: boolean; // 是否被重启
  is_open?: boolean; // 是否为开场白
  redo_msg_id?: number; // 重说Id
  timestamp?: number; // 消息时间戳（storage.chat.setLocalChatHistory记录在本地）
  attachment?: AttachmentImage | null;
  npc_id?: number;
  is_ready?: boolean;
  custom_mid?: string; // 环信消息的customMid

  // 扩展字段
  enable_typewriter?: boolean; // 是否启用打字机效果
  is_finished?: boolean; // 是否流式完成
  think_duration?: number;
  not_show_think_process?: boolean;
}

export enum EGender {
  NotDefined = 0,
  Male = 1,
  Female = 2,
  NoHuman = 3,
}

export enum ERoleType {
  Npc = 1,
  User = 2,
}

export interface IExampleDialogue {
  role_type?: ERoleType;
  message?: string;
}

export enum EPrivacySetting {
  Public = 1,
  Private = 2,
  Unlisted = 3,
}

export enum ENpcSourceType {
  PGC = 1,
  UGC = 2,
  ALT = 3,
}

export interface ILanguageData {
  desc?: string;
  prologue?: string;
}

export interface IMultiLanguageData {
  language_data: { [key: string]: ILanguageData };
}

export interface INpcMetaInfo {
  name?: string;
  gender?: EGender; // Assuming Gender is an Enum or a type.
  desc?: string;
  npc_tone?: { [key: string]: bigint };
  prologue?: string;
  example_dialogue_list?: IExampleDialogue[][];
  default_bgm?: string;
  prologue_wav?: string;
  prologue_wav_duration?: number;
  privacy_setting?: EPrivacySetting;
  npc_tone_name?: string;
  hidden_desc?: string;
  npc_source_type?: ENpcSourceType;
  npc_tag_list?: ITagInfo[];
  seo_keywords?: string[];
  multi_language_data?: IMultiLanguageData;
}

export interface INpcInfo {
  npc_id?: number;
  display_id?: string;
  meta_info?: INpcMetaInfo;
  avatar_info?: INpcAvatarInfo;
  author?: IAuthor;
  edit_version?: number;
  im_info?: IImInfo;
  moderation_status?: IModerationStatus;
  background_img?: IBackgroundImg;
  extra_info?: IExtraInfo;
}

export interface INpcInfoWithExtra {
  npc_info?: INpcInfo;
  follow_status?: EFollowType; // 关注情况
  statistic_info?: IStatisticInfo; // 统计信息
  privilege_info?: IPrivilegeInfo; // 用户认证信息
  has_chatted?: boolean; // 是否聊过
  meta_info?: INpcMetaInfo;
}

export interface IShareNpcData {
  im_account: string;
  im_password: string;
  npc_im_account: string;
  npc_name: string;
  npc_desc: string;
  base_img_url: string;
  head_img_url: string;
  prologue: string;
  prologue_wav: string;
  prologue_wav_duration: string;
  owner_id: number;
}

export interface IPostGetShareNpcDataReq {
  npc_id: number;
  share_user_id: number;
  need_im_info?: boolean; // 默认不需要
}

export interface INpcRelationStatusResp {
  level: number; //当前等级
  max_level: number; //最大登记
  current_value: number; //进度值
  target_value: number; //进度上限
}

export interface IGetNpcProfileReq {
  user_id?: number; // 用户ID
  npc_id?: number; // NPC ID
}

export interface IGetNpcCardDetailReq extends IGetNpcProfileReq {
  owner_card_pool_id?: number; // 卡牌id
}

export interface IGetNpcProfileResp {
  npc_info?: INpcInfoWithExtra; // NPC 信息
  npc_relation_status?: INpcRelationStatusResp; // NPC 关系状态
}

export enum EReportEntityType {
  NPC = 1,
  CARD = 2,
  CHAT_MSG = 3,
}

export interface ISubmitReportReq {
  entity_id: string; // npc_id/series_id/mid
  entity_type: EReportEntityType;
  reason: string; // 1,2,3
  other_reason?: string;
  additional_content?: string;
}

export enum EAsideScene {
  OpenCardBox = 1,
  OwnerNpcUpdated = 2,
  OthersNpcUpdate = 3,
  GenerateReplyFail = 4,
  MsgSlowDownAds = 10,
  NpcActiveMsg = 11,
  MsgCountAds = 12,
  SensitiveReply = 13,
  SensitiveWarning = 15,
  BranchAside = 16,
  ThrowMsg = 17,
  ThrowMsgWithAd = 18,
  NpcLevelUpgrade = 19,
  CollectAllSeriesCard = 21,
  StoryTips = 22,
}

export interface IAsideMsgInfo {
  aside_scene: EAsideScene;
  icon_uri: string;
  text_info: IAsideMsgTextInfo[];
  title: string;
}

export interface IRedoTalkingReq {
  user_id?: number;
  npc_id?: number;
  im_mid?: string;
  before_msg?: string;
  story_id?: number;
  group_id?: number;
}

export enum ETalkingCardType {
  RealCard = 1,
  DefaultCard = 2,
  AsideCard = 3,
}

export interface IRedoTalkingElem {
  // card_info: ICardInfo;
  redo_msg: string;
  redo_msg_id: number;
  talking_card_type: ETalkingCardType;
  is_sensitive: boolean;
}

export interface IGenerateRedoTalkingResp {
  im_mid: string;
  redo_talking_list: IRedoTalkingElem;
}

export interface IPickRedoTalkingReq {
  user_id: number;
  im_mid: string;
  redo_msg_id: number;
  npc_id: number;
}

export interface IPickRedoTalkingResp {
  voice_duration_ms: number;
}

export interface IPostQueryShareNumberResp {
  npc_share_number: number;
}

export interface IPostReportNpcShareReq {
  npc_id: number;
}

export interface IPostGetOwnerCreateCardListReq {
  user_id?: number;
  viewer_user_id?: number;
  page?: number;
  size?: number;
  npc_id?: number;
}

enum CardStatus {
  DoingModeration = 0,
  VisibleAll = 100,
  VisibleSelf = 200,
  VisibleNone = 300, // 卡牌已作废
  NotModeration = 400, // 未提交审核
}

export interface ImgInfo {
  img_url?: string;
  depth_img_url?: string;
  owner_card_pool_id?: number;
  card_status?: CardStatus;
  is_draw?: boolean;
  img_title?: string;
}

export interface IPostGetOwnerCreateCardListResp {
  owner_create_card_list: ImgInfo[];
  card_number: number;
  page: number;
  has_more: boolean;
}

export interface INpcCardDetailResp {
  story_info?: {
    title?: string;
    desc?: string;
    mission?: {
      mission_objective: string;
      mission_condition: string;
    };
  };
  card_name?: string;
  card_desc?: string;
  card_keywords?: string[];
  create_time?: number;
  owner_card_pool_id?: number;
}

export interface IGenerateSuggestTalkingReq {
  user_id?: number;
  npc_id?: number;
  im_mid?: string;
  is_redo_talking?: boolean; // 最新一条消息是否重说过
  regenerate?: boolean; // 是否重新生成
  reply_msg?: string; // 对应回复的消息，用来处理重说的场景
  slot_idx?: number; // 1-4
  story_id?: number;
  group_id?: number;
  is_soul_trans?: boolean; // 是否魂穿
  // 11: optional conversation_base.SoulTrans soul_trans_info; // 魂穿相关信息
}

enum TalkingCardType {
  RealCard = 1,
  DefaultCard = 2,
  AsideCard = 3, // 旁白卡
}

export interface SuggestTalkingElem {
  // 1: optional card_base.CardInfo card_info;
  msg: string;
  simple_msg: string;
  talking_card_type?: TalkingCardType;
  slot_idx: number;
  suggest_talking_id: number;
}

export interface IGenerateSuggestTalkingResp {
  suggest_talking_list?: SuggestTalkingElem[];
  im_mid?: string;
  is_member?: boolean; // 是否是会员
  has_more_card?: boolean; // 是否还有更多卡片
}

export enum RewriteUseRightsType {
  UseFreeTimes = 1, // 使用免费次数
  UseCard = 2, // 使用卡牌
  CardNotEnough = 3, // 卡牌数量不足
  UnLimitedTimes = 4, // 不限次数
}

export interface IRewriteMsgReq {
  npc_id: number;
  im_mid: string;
  msg_text: string;
  rewrite_use_rights_type: RewriteUseRightsType;
}

export interface IRewriteMsgResp {
  voice_duration_ms: number;
}

export interface IRevertConversationReq {
  npc_id?: number;
  im_mid?: string; // 在此id之后的消息都会被回溯
  is_prologue?: boolean;
  story_id?: number;
  topic_chat_id?: number;
  custom_mid?: string;
}

export enum ENpcAgentType {
  SingleChat = 1, // 正常聊天类
  SimulatorChat = 2, // 模拟器类
  Game = 3, // 游戏类
  UtilChat = 4, // 工具类
  ChitChat = 5, // 闲聊类
}
